/**
 * Single source of truth for how numbers and dates appear.
 *
 * Every figure on screen goes through here. Mixing raw values with
 * localised ones produced three different thousands separators on the
 * dashboard at once ("2 480", "65,000", "84.2") - a number the quarry
 * manager cannot read at a glance is a number that does not do its job.
 */

/** Dates only. Numbers are formatted explicitly - see below. */
const DATE_LOCALE = 'en-GB';
export const TIMEZONE = 'Africa/Windhoek';

const GROUP = '\u00A0'; // non-breaking space, so a figure never wraps mid-number
const DECIMAL = ',';

/**
 * Namibian convention: space thousands, comma decimal — 2 480,5.
 *
 * Deliberately NOT toLocaleString('en-ZA'): CLDR has changed what en-ZA
 * groups with, so the same build renders "2 480" on one device and "2,480"
 * on another. On a system whose whole purpose is unambiguous numbers, the
 * format cannot depend on the browser's ICU version.
 */
export function num(
  value: number | string | null | undefined,
  decimals = 0,
  fallback = '—',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return fallback;

  const fixed = Math.abs(n).toFixed(decimals);
  const [whole, fraction] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP);
  return `${n < 0 ? '-' : ''}${grouped}${fraction ? DECIMAL + fraction : ''}`;
}

/** Tonnes - whole numbers; nobody reads 2 480,37 t off a wall display. */
export const tonnes = (v: number | null | undefined, fallback = '—') => num(v, 0, fallback);

/** Hours and payloads - one decimal. */
export const hours = (v: number | null | undefined, fallback = '—') => num(v, 1, fallback);

/** Percentages - one decimal. */
export const pct = (v: number | null | undefined, fallback = '—') => num(v, 1, fallback);

export function date(value: string | Date | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString(DATE_LOCALE, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function dateTime(value: string | Date | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString(DATE_LOCALE, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

export function time(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  return value.slice(0, 5); // 07:00:00 -> 07:00
}

/** ISO date for <input type="date"> and for diary keys. */
export function isoDate(d: Date = new Date()): string {
  // The device's own calendar day, not UTC's. toISOString() is UTC, so in
  // Namibia (UTC+2) between midnight and 02:00 "today" used to be
  // yesterday — a night-shift diary captured after midnight defaulted to
  // the wrong date.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** "3 days ago", "in 5 days" - for ETAs and service windows. */
export function relativeDays(value: string | null | undefined): string {
  if (!value) return '—';
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return '—';
  const days = Math.round((target.getTime() - Date.now()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}

/** Sentence case for enum values: 'awaiting_parts' -> 'Awaiting parts'. */
export function humanise(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const s = value.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
